import { ArgsProps, IconType } from 'antd/es/notification/interface';
import classNames from 'classnames';
import { useCallback, useMemo } from 'react';
import { ToastOptions, TypeOptions, toast } from 'react-toastify';

import { ExplorerIcon } from '@/assets/svg';
import { useMediaQueryDevice } from '@/components/MediaQuery';
import { CloseButton } from '@/components/Notification';
import { mockNotiDontClose } from '@/constants/mock';
import { useTheme } from '@/features/global/hooks';
import { useEtherscanLink } from '@/hooks/web3/useChain';
import { checkLinkIsBlockExplorer } from '@/utils/chain';
import IconAddrLink from './assets/icon_acct_new.svg?react';
export interface INotificationArgs extends ArgsProps {
  type?: IconType;
  tx?: string;
  link?: string;
  taskId?: string;
}

const cachedNotify: { [tx: string]: string } = {};

export const useTxNotification = (
  chainId?: number,
): {
  open: (args: INotificationArgs) => void;
  success: (args: INotificationArgs) => void;
  info: (args: INotificationArgs) => void;
  warning: (args: INotificationArgs) => void;
  error: (args: INotificationArgs) => void;
} => {
  const { isMobile, deviceType } = useMediaQueryDevice();
  const { dataTheme } = useTheme();
  const getEtherscanLink = useEtherscanLink(chainId);
  /**
   * Notification wrapper, add tx open func
   * @param args Notification args
   */
  const openNotification = useCallback(
    (args: INotificationArgs): void => {
      let type = 'info';
      if (args.type) {
        type = args.type;
      }
      let finalLink = args.tx && getEtherscanLink(args.tx, 'transaction');
      if (args.link) {
        finalLink = args.link;
      }
      const isBlockExplorer = checkLinkIsBlockExplorer(finalLink || '');
      let icon = finalLink ? <IconAddrLink /> : null;
      if (isBlockExplorer) {
        icon = <ExplorerIcon />;
      }
      const toastDom = (
        <div className="syn-notification-content">
          <div className="syn-notification-content-title">
            <span> {args.message}</span>
            {icon}
          </div>
          {args.description && <div className="syn-notification-content-desc">{args.description}</div>}
        </div>
      );
      const toastConfig: ToastOptions = {
        onClick: () => {
          if (type === 'error') {
            return;
          }
          finalLink && window.open(finalLink);
        },
        className: classNames(deviceType, dataTheme, 'syn-notification-toaster'),
        closeButton: CloseButton,
        type: type as TypeOptions,
        position: isMobile ? 'top-center' : 'bottom-right',
        autoClose: mockNotiDontClose ? false : +(args.duration || 5000),
        onClose: () => {
          if (args.tx && cachedNotify[args.tx]) {
            delete cachedNotify[args.tx];
          }
        },
      };

      const cacheKey = args.taskId || args.tx;

      if (type === 'info' && (!cacheKey || !cachedNotify[cacheKey])) {
        const id = toast.loading(toastDom, toastConfig);
        cacheKey && (cachedNotify[cacheKey] = id.toString());
      } else if (cacheKey && cachedNotify[cacheKey]) {
        if (type !== 'info') {
          toast.update(cachedNotify[cacheKey], {
            ...toastConfig,
            render: toastDom,
            isLoading: false,
            closeButton: true,
            closeOnClick: true,
          });
        }
      } else {
        toast(toastDom, toastConfig);
      }

      // notification.open({
      //   message: args.message,
      //   description: args.description,
      //   icon: iconNode,
      //   duration: args.type === 'error' ? 30000 : args.type === 'info' ? 50000 : 50000,
      //   placement: isMobile ? 'topRight' : 'bottomRight',
      //   className: classNames(
      //     'syn-notification',
      //     `syn-notification_${args.type}`,
      //     (args.tx || args.link) && 'tx-notification',
      //   ),
      //   onClick: () => {
      //     if (args.link) {
      //       window.open(args.link);
      //     } else if (args.tx) {
      //       window.open(getEtherscanLink(args.tx, 'transaction'));
      //     }
      //   },
      // });
    },
    [deviceType, getEtherscanLink, isMobile, dataTheme],
  );

  /**
   *  add click
   */
  const txNotification = useMemo(() => {
    const res = {
      open: openNotification,
      success: openNotification,
      info: openNotification,
      warning: openNotification,
      error: openNotification,
    };

    const types: IconType[] = ['success', 'info', 'warning', 'error'];
    types.forEach((type: IconType) => {
      res[type] = (args: INotificationArgs): void =>
        res.open({
          ...args,
          type,
        });
    });
    return res;
  }, [openNotification]);

  return txNotification;
};
